import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  User, Car, House, Backpack, ShieldWarning, ChartLineUp, UsersThree, Briefcase, Bank,
  SignOut, ShieldCheck, CircleNotch, List, X,
} from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import MultiCharSwitcher from "./MultiCharSwitcher";

const nav = [
  { to: "/dashboard", label: "Charakter", icon: User, testId: "nav-character" },
  { to: "/vehicles", label: "Fahrzeuge", icon: Car, testId: "nav-vehicles" },
  { to: "/properties", label: "Immobilien", icon: House, testId: "nav-properties" },
  { to: "/inventory", label: "Inventar", icon: Backpack, testId: "nav-inventory" },
  { to: "/transactions", label: "Bank", icon: Bank, testId: "nav-transactions" },
  { to: "/jobs", label: "Fraktionen", icon: Briefcase, testId: "nav-jobs" },
  { to: "/record", label: "Polizei-Akte", icon: ShieldWarning, testId: "nav-record" },
  { to: "/stats", label: "Statistiken", icon: ChartLineUp, testId: "nav-stats" },
];

function NavContents({ user, onNavigate, logout, navigate }) {
  const isAdmin = user.role === "admin" || user.role === "owner";
  return (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-tactical-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-volt/50 flex items-center justify-center bg-volt/10">
            <ShieldCheck size={18} weight="duotone" className="text-volt" />
          </div>
          <div>
            <div className="font-display font-black text-lg leading-none">ROUTINE</div>
            <div className="font-mono text-[10px] text-white/40 tracking-widest">USER CONTROL</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        <div className="px-6 pb-2 font-mono text-[10px] text-white/30 uppercase tracking-widest">Charakter</div>
        {nav.map(({ to, label, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testId}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors duration-150 border-l-2 ${
                isActive
                  ? "border-volt bg-volt/5 text-white"
                  : "border-transparent text-white/60 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Icon size={18} weight="regular" />
            {label}
          </NavLink>
        ))}

        {isAdmin && (
          <>
            <div className="px-6 pt-6 pb-2 font-mono text-[10px] text-white/30 uppercase tracking-widest">Verwaltung</div>
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors duration-150 border-l-2 ${
                  isActive
                    ? "border-tactical-danger bg-tactical-danger/5 text-white"
                    : "border-transparent text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <UsersThree size={18} weight="regular" />
              Admin Panel
            </NavLink>
          </>
        )}
      </nav>

      <div className="border-t border-tactical-border p-4">
        <MultiCharSwitcher />
        <div className="flex items-center gap-3 mb-3 mt-3">
          <div className="w-9 h-9 border border-tactical-border bg-black flex items-center justify-center font-display font-bold">
            {user.username?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate" data-testid="sidebar-username">{user.display_name || user.username}</div>
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-wider">
              {user.role === "owner" ? "● OWNER" : user.role === "admin" ? "● ADMIN" : "● PLAYER"}
              {user.display_name && <span className="text-white/30 normal-case ml-1">· {user.username}</span>}
            </div>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={() => { logout(); navigate("/"); }}
          className="w-full flex items-center justify-center gap-2 border border-tactical-border hover:border-tactical-danger text-white/70 hover:text-tactical-danger py-2 text-xs font-mono uppercase tracking-widest transition-colors duration-150"
        >
          <SignOut size={14} /> Logout
        </button>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white/50 font-mono text-sm">
        <CircleNotch size={20} className="animate-spin mr-3" /> Loading terminal...
      </div>
    );
  }
  if (!user) { navigate("/"); return null; }

  return (
    <div className="min-h-screen flex bg-tactical-bg text-white grain">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-tactical-border bg-tactical-surface flex-col sticky top-0 h-screen">
        <NavContents user={user} onNavigate={() => {}} logout={logout} navigate={navigate} />
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-40 border-b border-tactical-border bg-black/80 backdrop-blur-xl flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 border border-volt/50 flex items-center justify-center bg-volt/10">
              <ShieldCheck size={16} weight="duotone" className="text-volt" />
            </div>
            <div>
              <div className="font-display font-black text-sm leading-none">ROUTINE</div>
              <div className="font-mono text-[9px] text-white/40 tracking-widest">USER CONTROL</div>
            </div>
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-nav-toggle" className="w-10 h-10 border border-tactical-border flex items-center justify-center hover:border-white transition-colors">
                <List size={20} weight="bold" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0 bg-tactical-surface border-tactical-border">
              <NavContents user={user} onNavigate={() => setOpen(false)} logout={logout} navigate={navigate} />
            </SheetContent>
          </Sheet>
        </div>

        <Outlet />
      </main>
    </div>
  );
}
